/*
 * FG Offer Builder - new-customer offer landing page.
 * See sections/fg-offer-builder.liquid for the markup and the checkout rationale.
 *
 * Prices are handled in cents throughout. The page only DISPLAYS tier pricing;
 * what the customer is actually charged comes from whatever discount the tier's
 * `code` points at (applied via /checkout?discount=CODE). Keep the two in sync.
 */
(function () {
  'use strict';

  const STORAGE_KEY = 'fg-offer-builder-v1';
  const cents = (v) => Math.round((parseFloat(v) || 0) * 100);
  const money = (c) => '$' + (c / 100).toFixed(2);
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));

  const ICONS = {
    gift: '<path d="M4 11h16v9H4zM3 7h18v4H3zM12 7v13M12 7c-1.5-3-5-3.5-5-1.2C7 7 9.5 7 12 7zm0 0c1.5-3 5-3.5 5-1.2C17 7 14.5 7 12 7z"/>',
    truck: '<path d="M2 6h11v10H2zM13 9h4l4 4v3h-8zM6 19a2 2 0 1 0 0-.01M17 19a2 2 0 1 0 0-.01"/>',
    straw: '<path d="M15 2l-3 6M7 8h10l-1.5 13h-7zM7.6 13h8.8"/>',
    tote: '<path d="M4 8h16l-1.5 13h-13zM9 8V6a3 3 0 0 1 6 0v2"/>',
    cup: '<path d="M6 5h12l-1.6 15H7.6zM6 9h12M12 5V2"/>'
  };
  const icon = (name) => `<svg viewBox="0 0 24 24" width="36" height="36" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${ICONS[name] || ICONS.gift}</svg>`;

  class FGOffer {
    constructor(root) {
      this.root = root;
      const section = root.closest('.shopify-section') || document;
      this.products = JSON.parse(section.querySelector('[data-fg-offer-products]').textContent);
      const cfg = JSON.parse(section.querySelector('[data-fg-offer-config]').textContent);
      this.cfg = cfg;
      // 'health' (Health Benefits) or 'flavor' (Flavor Profile): the Liquid fills
      // each product's `benefits` from the matching metafield, so everything that
      // scores against the quiz answers works the same either way.
      this.flavorQuiz = cfg.quizType === 'flavor';
      // Separate saved answers per quiz type so health goals never land on flavor pills.
      this.storageKey = this.flavorQuiz ? `${STORAGE_KEY}-flavor` : STORAGE_KEY;
      this.byId = new Map(this.products.map((p) => [p.id, p]));

      this.tiers = cfg.tiers
        .map((t) => ({ qty: t.qty, price: cents(t.price), shipping: cents(t.shipping), badge: t.badge, code: t.code }))
        .filter((t) => t.qty > 0)
        .sort((a, b) => a.qty - b.qty);
      this.minQty = this.tiers.length ? this.tiers[0].qty : 1;
      this.gifts = cfg.gifts;
      this.categories = cfg.categories.map((c) => ({ ...c, claims: (c.claims || []).map((s) => s.trim()).filter(Boolean) }));
      this.regularPrice = cfg.regularPrice;
      this.regularShipping = cents(cfg.regularShipping);

      this.assignPrimaryCategories();

      // State
      this.target = this.tierFor(cfg.defaultTier) ? cfg.defaultTier : (this.tiers[Math.min(2, this.tiers.length - 1)] || {}).qty;
      this.qty = {};
      this.touched = false;
      this.filter = 'all';
      this.giftChoice = {};
      this.answers = { goals: [], allergens: [], skip: [] };
      this.answered = false;
      this.stage = 'box';
      this.showExcluded = false;
      this.ingredientQuery = '';

      // Best-seller rank: the default-picks list first (it's ordered by sales),
      // then everything else in collection order. Breaks ties when building a box.
      const pickIds = cfg.defaultPicks || [];
      this.rank = new Map();
      pickIds.forEach((id, i) => this.rank.set(id, i));
      this.products.forEach((p, i) => { if (!this.rank.has(p.id)) this.rank.set(p.id, pickIds.length + i); });

      const urlPack = parseInt(new URLSearchParams(location.search).get('pack'), 10);
      this.restore();
      if (urlPack && this.tierFor(urlPack)) { this.target = urlPack; if (!this.touched) this.qty = {}; }

      this.cacheEls();
      this.bind();
      this.renderOffer();
      this.setupSticky();
    }

    /* ---------- data helpers ---------- */

    tierFor(qty) { return this.tiers.find((t) => t.qty === qty); }

    // Highest tier the customer's actual count qualifies for.
    activeTier(count) {
      let active = null;
      this.tiers.forEach((t) => { if (count >= t.qty) active = t; });
      return active;
    }

    count() { return Object.values(this.qty).reduce((a, b) => a + b, 0); }

    totalFor(count, tier) { return tier ? tier.price * count + tier.shipping : 0; }

    // Regular shipping for a box this size. A free-shipping quantity of 0 means
    // shipping always counts at the regular rate.
    regularShipFor(count) {
      const freeQty = this.cfg.regularFreeShipQty;
      return freeQty > 0 && count >= freeQty ? 0 : this.regularShipping;
    }

    regularFor(count) { return this.regularPrice * count + this.regularShipFor(count); }

    giftsFor(tierQty) {
      return this.gifts.filter((g) => tierQty >= g.unlockQty && !(g.hideQty && tierQty >= g.hideQty));
    }

    giftValue(g) { return cents(String(g.value || '').replace(/[^0-9.]/g, '')); }

    // What the customer saves vs. regular pricing: the per-smoothie difference
    // plus any shipping they'd otherwise pay. Gifts are reported separately.
    savingsFor(count, tier) {
      if (!tier) return 0;
      const regShip = this.regularShipFor(count);
      return Math.max(0, (this.regularPrice - tier.price) * count) + Math.max(0, regShip - tier.shipping);
    }

    giftSummary(tierQty) {
      const gifts = this.giftsFor(tierQty).filter((g) => !g.isShipping);
      const value = gifts.reduce((sum, g) => sum + this.giftValue(g), 0);
      if (!gifts.length) return '';
      return `${gifts.length} free gift${gifts.length > 1 ? 's' : ''}${value ? ` (${money(value)} value)` : ''}`;
    }

    // Gifts shown on the offer card: every gift that isn't superseded at this tier.
    // Only one shipping tile at a time: the best one this pack has unlocked (or
    // the first one, if none are unlocked yet). So the 10-pack shows Half-Price
    // Shipping, not Half-Price plus a locked Free Shipping.
    visibleGifts(tierQty) {
      const gifts = this.gifts.filter((g) => !(g.hideQty && tierQty >= g.hideQty));
      const ship = gifts.filter((g) => g.isShipping);
      const unlocked = ship.filter((g) => tierQty >= g.unlockQty);
      const keep = unlocked.length ? unlocked[unlocked.length - 1] : ship[0];
      return gifts.filter((g) => !g.isShipping || g === keep);
    }

    assignPrimaryCategories() {
      this.products.forEach((p) => {
        const benefits = p.benefits || [];
        p.primary = this.categories.findIndex((c) => c.claims.some((cl) => benefits.includes(cl)));
      });
    }

    excluded(p) {
      const a = this.answers;
      return (p.allergens || []).some((x) => a.allergens.includes(x)) || (p.allIngredients || []).some((x) => a.skip.includes(x));
    }

    // Same rules as the Flavor Quiz (assets/fg-flavor-quiz.js buildResult) for a
    // smoothies-only box with "Mix It Up" variety: hard-filter allergens and
    // skipped ingredients, score by matching health goals, tiebreak by
    // best-seller rank, cap distinct flavors by box size, spread evenly.
    // With no goals picked every score is 0, so it's simply best sellers.
    seed(total) {
      this.qty = {};
      const scored = this.products
        .filter((p) => !this.excluded(p))
        .map((p) => ({ p, score: (p.benefits || []).filter((b) => this.answers.goals.includes(b)).length }))
        .sort((a, b) => b.score - a.score || this.rank.get(a.p.id) - this.rank.get(b.p.id));
      const ceiling = total <= 15 ? 1 : total <= 24 ? 2 : 3;
      const distinct = Math.min(Math.ceil(total / ceiling), this.cfg.varietyCount || 30, scored.length);
      const pool = scored.slice(0, distinct).map((e) => e.p);
      let remaining = total;
      let guard = 0;
      // Round-robin so stock caps are respected and leftovers spill to the next flavor.
      while (remaining > 0 && pool.length && guard < 1000) {
        let placed = false;
        for (const p of pool) {
          if (remaining <= 0) break;
          const cur = this.qty[p.id] || 0;
          if (p.maxQty != null && cur >= p.maxQty) continue;
          this.qty[p.id] = cur + 1;
          remaining--; placed = true;
        }
        if (!placed) break;
        guard++;
      }
      this.touched = false;
    }

    /* ---------- persistence (per-viewer convenience only) ---------- */

    save() {
      try { localStorage.setItem(this.storageKey, JSON.stringify({ target: this.target, qty: this.qty, touched: this.touched, giftChoice: this.giftChoice, giftPicked: this.giftPicked || {}, answers: this.answers, answered: this.answered, t: Date.now() })); } catch (e) { /* storage unavailable */ }
    }

    restore() {
      try {
        const s = JSON.parse(localStorage.getItem(this.storageKey) || 'null');
        if (!s || Date.now() - s.t > 1000 * 60 * 60 * 24 * 7) return;
        if (this.tierFor(s.target)) this.target = s.target;
        if (s.touched && s.qty) {
          // Drop anything that has since sold out or left the collection.
          Object.entries(s.qty).forEach(([id, n]) => { if (this.byId.has(+id) && n > 0) this.qty[+id] = n; });
          this.touched = this.count() > 0;
        }
        this.giftChoice = s.giftChoice || {};
        this.giftPicked = s.giftPicked || {};
        if (s.answers) { this.answers = { goals: s.answers.goals || [], allergens: s.answers.allergens || [], skip: s.answers.skip || [] }; this.answered = !!s.answered; }
      } catch (e) { /* ignore */ }
    }

    /* ---------- DOM ---------- */

    cacheEls() {
      const $ = (s) => this.root.querySelector(s);
      this.els = {
        tiers: $('[data-fg-tiers]'), gifts: $('[data-fg-gifts]'), total: $('[data-fg-total]'), compare: $('[data-fg-compare]'),
        totalLabel: $('[data-fg-total-label]'), savings: $('[data-fg-savings]'), picker: $('[data-fg-picker]'),
        pickerTitle: $('[data-fg-picker-title]'), chips: $('[data-fg-tier-chips]'), filters: $('[data-fg-filters]'),
        list: $('[data-fg-list]'), giftChoice: $('[data-fg-gift-choice]'), progress: $('[data-fg-progress]'),
        barMsg: $('[data-fg-bar-msg]'), barGifts: $('[data-fg-bar-gifts]'), checkout: $('[data-fg-checkout]'),
        checkoutLabel: $('[data-fg-checkout-label]'), countEl: $('[data-fg-count]'), toast: $('[data-fg-toast]'),
        sticky: $('[data-fg-sticky]'), subtitle: $('[data-fg-picker-subtitle]'), hiddenNote: $('[data-fg-hidden-note]'),
        goalPills: $('[data-fg-goal-pills]'), allergenPills: $('[data-fg-allergen-pills]'), ingredientPills: $('[data-fg-ingredient-pills]'),
        skipChips: $('[data-fg-skip-chips]'), search: $('[data-fg-ingredient-search]'), quizModal: $('[data-fg-quiz-modal]'), infoModal: $('[data-fg-info-modal]'), infoTitle: $('[data-fg-info-title]'), infoBody: $('[data-fg-info-body]'), quizStep: $('[data-fg-quiz-step]'), quizTitle: $('[data-fg-quiz-title]'),
        quizSub: $('[data-fg-quiz-sub]'), quizProgress: $('[data-fg-quiz-progress]'),
        quizNext: $('[data-fg-quiz-next]'), quizSkip: $('[data-fg-quiz-skip]'), stickyText: $('[data-fg-sticky-text]'), mainImg: $('[data-fg-main-img]')
      };
    }

    bind() {
      this.root.addEventListener('click', (e) => {
        const t = e.target.closest('button, a');
        if (!t || !this.root.contains(t)) return;
        if (t.matches('[data-fg-tier]')) this.selectTier(+t.dataset.fgTier, t.closest('[data-fg-tier-chips]') != null);
        else if (t.matches('[data-fg-open-picker]')) this.openPicker();
        else if (t.matches('[data-fg-info]')) this.openInfo(+t.dataset.fgInfo);
        else if (t.matches('[data-fg-info-close]')) this.closeInfo();
        else if (t.matches('[data-fg-close-picker]')) this.closePicker();
        else if (t.matches('[data-fg-quiz-close]')) this.closeQuiz();
        else if (t.matches('[data-fg-quiz-back]')) { if (this.stage === 'avoid') this.setStage('goals'); else this.closeQuiz(); }
        else if (t.matches('[data-fg-toggle]')) this.toggleAnswer(t.dataset.fgToggle, t.dataset.value);
        else if (t.matches('[data-fg-quiz-next]')) this.quizNext(false);
        else if (t.matches('[data-fg-quiz-skip]')) {
          if (t.dataset.clear) {
            if (this.stage === 'goals') this.answers.goals = []; else { this.answers.allergens = []; this.answers.skip = []; }
            this.setStage(this.stage); this.save();
          } else this.quizNext(true);
        }
        else if (t.matches('[data-fg-retake]')) this.openQuiz('goals');
        else if (t.matches('[data-fg-show-excluded]')) { this.showExcluded = !this.showExcluded; this.renderList(); }
        else if (t.matches('[data-fg-step]')) this.step(+t.dataset.id, +t.dataset.fgStep);
        else if (t.matches('[data-fg-reset]')) { this.seed(this.target); this.showToast(this.answers.goals.length ? `Rebuilt around your ${this.flavorQuiz ? 'flavors' : 'health goals'}` : 'Rebuilt with our best sellers'); this.afterChange(true); }
        else if (t.matches('[data-fg-clear]')) { this.qty = {}; this.touched = true; this.afterChange(true); }
        else if (t.matches('[data-fg-gift-pick]')) {
          this.giftChoice[t.dataset.gift] = +t.dataset.variant;
          this.giftPicked = Object.assign(this.giftPicked || {}, { [t.dataset.gift]: true });
          this.save(); this.renderGiftChoice(); this.renderBar();
        }
        else if (t.matches('[data-fg-gift-jump]')) {
          const top = this.els.giftChoice.getBoundingClientRect().top - this.els.picker.getBoundingClientRect().top + this.els.picker.scrollTop - 130;
          this.els.picker.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
          this.els.giftChoice.classList.remove('is-flash'); void this.els.giftChoice.offsetWidth; this.els.giftChoice.classList.add('is-flash');
        }
        else if (t.matches('[data-fg-checkout]')) this.checkout();
        else if (t.matches('[data-fg-thumb]')) this.swapImage(t);
      });

      this.els.quizModal.querySelector('.fg-quiz-modal__overlay').addEventListener('click', () => this.closeQuiz());
      this.els.infoModal.querySelector('[data-fg-info-overlay]').addEventListener('click', () => this.closeInfo());

      // Any link on the page to #select-flavors (e.g. a CTA banner lower down) opens the picker.
      document.addEventListener('click', (e) => {
        const a = e.target.closest('a[href$="#select-flavors"]');
        if (!a) return;
        e.preventDefault();
        this.openPicker();
      });

      this.root.addEventListener('change', (e) => {
        if (!e.target.matches('[data-fg-filter-select]')) return;
        this.filter = e.target.value;
        this.renderList();
      });

      if (this.els.search) this.els.search.addEventListener('input', () => { this.ingredientQuery = this.els.search.value.trim().toLowerCase(); this.renderIngredientPills(); });

      window.addEventListener('popstate', (e) => {
        const s = e.state || {};
        if (!s.fgInfo && !this.els.infoModal.hidden) this.hideInfo();
        if (!s.fgQuiz && !this.els.quizModal.hidden) this.hideQuiz();
        if (!s.fgPicker && !this.els.picker.hidden) this.hidePicker();
      });
      // Back from checkout can restore this page frozen mid-"loading".
      window.addEventListener('pageshow', (e) => {
        if (e.persisted) { this.els.checkout.removeAttribute('data-loading'); this.renderBar(); }
      });
      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        if (!this.els.infoModal.hidden) this.closeInfo();
        else if (!this.els.quizModal.hidden) this.closeQuiz();
        else if (!this.els.picker.hidden) this.closePicker();
      });
      if (location.hash === '#select-flavors') setTimeout(() => this.openPicker(), 0);
    }

    swapImage(btn) {
      const img = this.els.mainImg.querySelector('img');
      if (img) { img.removeAttribute('srcset'); img.src = btn.dataset.src; }
      this.root.querySelectorAll('[data-fg-thumb]').forEach((b) => b.classList.toggle('is-active', b === btn));
    }

    /* ---------- step 1: offer ---------- */

    shipLabel(t) {
      const reg = this.regularShipFor(t.qty);
      const was = reg > t.shipping ? `<s>${money(reg)}</s> ` : '';
      if (!t.shipping) return `${was}Free shipping`;
      return `${was}${money(t.shipping)} ${reg > t.shipping && t.shipping * 2 >= reg - 1 && t.shipping * 2 <= reg + 1 ? 'half-price shipping' : 'shipping'}`;
    }

    // "FREE $9.99" for gifts; the half-price shipping tile shows what they pay
    // instead: "$4.99" with the regular shipping price crossed out.
    giftTagHTML(g) {
      const t = this.tierFor(g.unlockQty);
      if (g.isShipping && t && t.shipping > 0) return `${money(t.shipping)} <s>${money(this.regularShipping)}</s>`;
      return `FREE${g.value ? ` <s>${esc(g.value)}</s>` : ''}`;
    }

    tierCardHTML(t, compact) {
      const sel = t.qty === this.target;
      const regular = this.regularFor(t.qty);
      const total = this.totalFor(t.qty, t);
      const pct = regular > total ? Math.round((1 - total / regular) * 100) : 0;
      if (compact) {
        return `<button type="button" role="radio" aria-checked="${sel}" class="fg-chip${sel ? ' is-selected' : ''}" data-fg-tier="${t.qty}">${t.qty} <span>${money(t.price)}/ea</span></button>`;
      }
      // The smallest pack leads with the guarantee, not a 5% saving.
      const save = pct >= 3 && t.qty !== this.tiers[0].qty ? `Save ${pct}%` : '';
      const badge = t.badge || save;
      return `<button type="button" role="radio" aria-checked="${sel}" class="fg-tier${sel ? ' is-selected' : ''}" data-fg-tier="${t.qty}">
        ${badge ? `<span class="fg-tier__badge">${esc(badge)}</span>` : ''}
        <span class="fg-tier__qty"><b>${t.qty}</b> smoothies</span>
        <span class="fg-tier__price">${money(t.price)} / smoothie${save && t.badge ? ` <em class="fg-tier__save">${save}</em>` : ''}</span>
        <span class="fg-tier__ship">${this.shipLabel(t)}</span>
        ${(() => { const n = this.giftsFor(t.qty).filter((g) => !g.isShipping).length; return n ? `<span class="fg-tier__gifts">+ ${n} free gift${n > 1 ? 's' : ''}</span>` : ''; })()}
      </button>`;
    }

    giftTileHTML(g, tierQty) {
      const unlocked = tierQty >= g.unlockQty;
      const media = g.image ? `<img src="${esc(g.image)}" alt="" loading="lazy" width="120" height="120">` : icon(g.icon);
      return `<div class="fg-gift${unlocked ? '' : ' is-locked'}">
        <span class="fg-gift__tag">${this.giftTagHTML(g)}</span>
        <span class="fg-gift__media">${media}</span>
        <span class="fg-gift__title">${esc(g.title)}</span>
        ${unlocked ? '<span class="fg-gift__included">✓ Included</span>' : `<span class="fg-gift__lock">Unlocks at ${g.unlockQty}</span>`}
      </div>`;
    }

    renderOffer() {
      const t = this.tierFor(this.target);
      this.els.tiers.innerHTML = this.tiers.map((x) => this.tierCardHTML(x, false)).join('');
      this.els.gifts.innerHTML = this.visibleGifts(this.target).map((g) => this.giftTileHTML(g, this.target)).join('');
      if (!t) return;
      const total = this.totalFor(t.qty, t);
      const regular = this.regularFor(t.qty);
      this.els.totalLabel.textContent = `${t.qty} smoothies`;
      this.els.total.textContent = money(total);
      this.els.compare.textContent = regular > total ? money(regular) : '';
      const saved = this.savingsFor(t.qty, t);
      this.els.savings.textContent = [saved ? `You save ${money(saved)}` : '', this.giftSummary(t.qty)].filter(Boolean).join(' + ');
      if (this.els.stickyText) this.els.stickyText.textContent = `${t.qty} smoothies · ${money(total)}`;
    }

    selectTier(qty, fromPicker) {
      if (!this.tierFor(qty)) return;
      this.target = qty;
      // Untouched picks follow the pack size; hand-picked boxes are left alone.
      if (!this.touched) this.seed(qty);
      this.save();
      this.renderOffer();
      if (fromPicker || !this.els.picker.hidden) this.renderPicker();
    }

    setupSticky() {
      const cta = this.root.querySelector('.fg-offer__summary [data-fg-open-picker]');
      if (!cta || !('IntersectionObserver' in window) || !this.els.sticky) return;
      new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.els.sticky.classList.toggle('is-visible', show);
        this.els.sticky.setAttribute('aria-hidden', String(!show));
        this.els.sticky.querySelector('button').tabIndex = show ? 0 : -1;
      }).observe(cta);
    }

    /* ---------- step 2: picker ---------- */

    // "Select Flavors": first time -> the 2-question pop-up; once they've answered
    // or hand-picked anything -> straight to the flavor list.
    openPicker() {
      if (this.touched || this.answered) this.showList();
      else this.openQuiz('goals');
    }

    showList() {
      if (!this.touched && this.count() !== this.target) this.seed(this.target);
      this.stage = 'box';
      this.renderPicker();
      this.els.picker.hidden = false;
      document.documentElement.classList.add('fg-picker-open');
      this.els.picker.scrollTop = 0;
      if (!(history.state && history.state.fgPicker)) history.pushState({ fgPicker: true }, '', location.pathname + location.search + '#select-flavors');
      this.els.picker.querySelector('[data-fg-close-picker]').focus({ preventScroll: true });
    }

    closePicker() {
      if (history.state && history.state.fgPicker) history.back(); // popstate hides it
      else this.hidePicker();
    }

    hidePicker() {
      this.els.picker.hidden = true;
      document.documentElement.classList.remove('fg-picker-open');
      if (location.hash === '#select-flavors') history.replaceState(null, '', location.pathname + location.search);
      this.renderOffer();
      const cta = this.root.querySelector('.fg-offer__summary [data-fg-open-picker]');
      if (cta) cta.focus({ preventScroll: true });
    }

    /* ---------- mini quiz (health goals -> things to skip -> built box) ---------- */

    // Nutrition facts: the printed label photo (custom.nutrition_label) plus the
    // ingredients and allergens as real text, since the label is only an image.
    openInfo(id) {
      let p = this.byId.get(id);
      // Delite choices aren't in the box list; look them up by variant id.
      if (!p) p = this.gifts.reduce((f, g) => f || (g.choices || []).find((c) => c.variantId === id), null);
      if (!p) return;
      this.infoReturn = document.activeElement;
      this.els.infoTitle.textContent = p.title;
      // The tabular panel already prints ingredients + allergens, so it's shown on
      // its own; the same info goes in the alt text for screen readers.
      const alt = `Nutrition Facts for ${p.title}. Ingredients: ${(p.allIngredients || []).join(', ')}.`
        + ((p.allergens || []).length ? ` Contains: ${p.allergens.join(', ')}.` : '');
      this.els.infoBody.innerHTML = p.nutrition
        ? `<img class="fg-info-modal__label${p.nutritionTabular ? ' is-tabular' : ''}" src="${esc(p.nutrition)}" alt="${esc(alt)}">`
        : `<p class="fg-info-modal__ingredients"><strong>Ingredients:</strong> ${(p.allIngredients || []).map(esc).join(', ')}</p>`;
      this.els.infoModal.hidden = false;
      document.documentElement.classList.add('fg-quiz-open');
      this.pushLayer('fgInfo');
      this.els.infoModal.querySelector('[data-fg-info-close]').focus({ preventScroll: true });
    }

    closeInfo() {
      if (history.state && history.state.fgInfo) history.back(); // popstate hides it
      else this.hideInfo();
    }

    hideInfo() {
      this.els.infoModal.hidden = true;
      if (this.els.quizModal.hidden) document.documentElement.classList.remove('fg-quiz-open');
      if (this.infoReturn) this.infoReturn.focus({ preventScroll: true });
    }

    openQuiz(stage) {
      this.els.quizModal.hidden = false;
      document.documentElement.classList.add('fg-quiz-open');
      this.pushLayer('fgQuiz');
      this.setStage(stage);
      this.els.quizModal.querySelector('[data-fg-quiz-close]:not(.fg-quiz-modal__overlay)').focus({ preventScroll: true });
    }

    // Each layer (quiz, nutrition pop-up, picker) owns a history entry so the
    // browser Back button closes the top layer instead of leaving the page.
    pushLayer(key) {
      const s = Object.assign({}, history.state || {});
      if (s[key]) return;
      s[key] = true;
      history.pushState(s, '', location.pathname + location.search + '#select-flavors');
    }

    closeQuiz() {
      if (history.state && history.state.fgQuiz) history.back(); // popstate hides it
      else this.hideQuiz();
    }

    // "Build My Box": swap the quiz layer for the picker layer in place.
    finishQuiz() {
      const s = history.state || {};
      this.hideQuiz();
      if (s.fgQuiz && !s.fgPicker) history.replaceState({ fgPicker: true }, '', location.pathname + location.search + '#select-flavors');
      else if (s.fgQuiz) history.back();
      this.showList();
    }

    hideQuiz() {
      this.els.quizModal.hidden = true;
      document.documentElement.classList.remove('fg-quiz-open');
      this.stage = 'box';
    }

    setStage(stage) {
      if (stage === 'box') { this.finishQuiz(); return; }
      this.stage = stage;
      this.els.quizModal.querySelectorAll('.fg-quiz-stage').forEach((el) => { el.hidden = el.dataset.fgStage !== stage; });
      const c = this.cfg.copy || {};
      const goals = stage === 'goals';
      this.els.quizStep.textContent = goals ? 'Question 1 of 2' : 'Question 2 of 2';
      this.els.quizTitle.innerHTML = goals ? (c.goalsHeading || 'Any health goals?') : (c.avoidHeading || 'Anything to skip?');
      this.els.quizSub.textContent = goals ? (c.goalsSub || '') : (c.avoidSub || '');
      this.els.quizNext.textContent = goals ? 'Continue' : 'Build My Box';
      this.els.quizProgress.style.width = goals ? '50%' : '100%';
      if (goals) this.renderGoalPills(); else { this.renderAllergenPills(); this.renderIngredientPills(); }
      this.updateQuizButtons();
      const body = this.els.quizModal.querySelector('.fg-quiz-modal__body');
      if (body) body.scrollTop = 0;
    }

    quizNext(skip) {
      if (this.stage === 'goals') {
        if (skip) this.answers.goals = [];
        this.setStage('avoid');
        return;
      }
      if (skip) { this.answers.allergens = []; this.answers.skip = []; }
      this.answered = true;
      this.showExcluded = false;
      this.filter = 'all';
      this.seed(this.target);
      this.save();
      this.setStage('box');
    }

    // Live check on question 2: how many smoothies survive the skips. Zero blocks
    // "Build My Box" so nobody lands on an empty list.
    eligibleCount() { return this.products.filter((p) => !this.excluded(p)).length; }

    renderAvoidWarn() {
      let el = this.els.quizWarn;
      if (!el) {
        el = this.els.quizWarn = document.createElement('p');
        el.className = 'fg-quiz-warn';
        el.setAttribute('role', 'status');
        this.els.quizModal.querySelector('.fg-quiz-modal__foot').insertAdjacentElement('beforebegin', el);
      }
      const n = this.eligibleCount();
      const total = this.products.length;
      const on = this.stage === 'avoid' && (this.answers.allergens.length + this.answers.skip.length) > 0;
      el.hidden = !on || n >= 6;
      el.classList.toggle('is-blocked', n === 0);
      if (!el.hidden) {
        el.textContent = n === 0
          ? 'No smoothies are left with all of these skipped. Remove one or two to keep going.'
          : `Only ${n} of our ${total} smoothies are left. Your box will repeat them, or remove a skip for more variety.`;
      }
      this.els.quizNext.disabled = this.stage === 'avoid' && n === 0;
    }

    updateQuizButtons() {
      this.renderAvoidWarn();
      const a = this.answers;
      const any = this.stage === 'goals' ? a.goals.length : a.allergens.length + a.skip.length;
      this.els.quizSkip.textContent = this.stage === 'goals' ? (any ? 'Clear' : 'Skip') : (any ? 'Clear' : 'Nothing to skip');
      this.els.quizSkip.onclick = null;
      if (any) this.els.quizSkip.dataset.clear = '1'; else delete this.els.quizSkip.dataset.clear;
    }

    toggleAnswer(kind, value) {
      const list = this.answers[kind];
      const i = list.indexOf(value);
      if (i === -1) list.push(value); else list.splice(i, 1);
      if (kind === 'goals') this.renderGoalPills();
      else if (kind === 'allergens') this.renderAllergenPills();
      else this.renderIngredientPills();
      this.updateQuizButtons();
      this.save();
    }

    pillHTML(kind, value, label) {
      const on = this.answers[kind].includes(value);
      return `<button type="button" class="fg-pill${on ? ' is-selected' : ''}" aria-pressed="${on}" data-fg-toggle="${kind}" data-value="${esc(value)}">${esc(label || value)}</button>`;
    }

    renderGoalPills() {
      // Live from the smoothies' Health Benefits (or Flavor Profile) metafield, most common first.
      const counts = {};
      this.products.forEach((p) => (p.benefits || []).forEach((b) => { counts[b] = (counts[b] || 0) + 1; }));
      const goals = Object.keys(counts).sort((a, b) => counts[b] - counts[a] || a.localeCompare(b));
      this.els.goalPills.innerHTML = goals.map((g) => this.pillHTML('goals', g)).join('');
    }

    renderAllergenPills() {
      const priority = ['Almond', 'Cashew', 'Hazelnut', 'Pecan', 'Walnut', 'Peanut', 'Soy', 'Caffeine'];
      const rank = (t) => (priority.indexOf(t) === -1 ? priority.length : priority.indexOf(t));
      const set = new Set();
      this.skippables().forEach((p) => (p.allergens || []).forEach((a) => set.add(a)));
      this.els.allergenPills.innerHTML = [...set].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b)).map((a) => this.pillHTML('allergens', a)).join('');
    }

    // Smoothies plus the free-gift choices (Delites), so a Peanut or Walnut skip is
    // offered even when no smoothie contains it.
    skippables() {
      return this.products.concat(...this.gifts.map((g) => g.choices || []));
    }

    renderIngredientPills() {
      const set = new Set();
      this.skippables().forEach((p) => (p.allIngredients || []).forEach((x) => set.add(x)));
      const all = [...set].sort((a, b) => a.localeCompare(b));
      const q = this.ingredientQuery;
      const shown = all.filter((x) => !this.answers.skip.includes(x) && (!q || x.toLowerCase().includes(q)));
      this.els.ingredientPills.innerHTML = shown.length ? shown.map((x) => this.pillHTML('skip', x)).join('') : '<p class="fg-quiz-stage__empty">No matching ingredients.</p>';
      this.els.skipChips.innerHTML = this.answers.skip.map((x) => `<button type="button" class="fg-skip-chip" data-fg-toggle="skip" data-value="${esc(x)}" aria-label="Stop skipping ${esc(x)}">${esc(x)} <span aria-hidden="true">×</span></button>`).join('');
    }

    renderPicker() {
      this.els.chips.innerHTML = this.tiers.map((t) => this.tierCardHTML(t, true)).join('');
      this.renderFilters();
      this.renderList();
      this.renderGiftChoice();
      this.renderBar();
    }

    // The benefit filter was removed (the box is already built around their
    // goals), so the list always shows every flavor.
    renderFilters() {
      this.filter = 'all';
      this.els.filters.innerHTML = '';
      this.els.filters.hidden = true;
    }

    claimCount(claim) {
      if (!this._claimCounts) {
        this._claimCounts = {};
        this.products.forEach((p) => (p.benefits || []).forEach((b) => { this._claimCounts[b] = (this._claimCounts[b] || 0) + 1; }));
      }
      return this._claimCounts[claim] || 0;
    }

    matchScore(p) { return (p.benefits || []).filter((b) => this.answers.goals.includes(b)).length; }

    byMatch(a, b) { return this.matchScore(b) - this.matchScore(a) || this.rank.get(a.id) - this.rank.get(b.id); }

    // One flavor as a list row: image | name, ingredients, rating + why | quantity.
    // Chips show only the health goals this smoothie matches, so they always
    // explain why it's in the box. No goals picked -> the smoothie type instead.
    cardHTML(p) {
      const n = this.qty[p.id] || 0;
      // Up to 4 benefits (the 4th is hidden on phones): their matched goals first, then this smoothie's most
      // distinctive ones (claims few other smoothies carry), so rows differ.
      const goals = this.answers.goals;
      const shown = (p.benefits || []).slice()
        .sort((a, b) => (goals.includes(b) - goals.includes(a)) || (this.claimCount(a) - this.claimCount(b)) || a.localeCompare(b))
        .slice(0, 4);
      const atMax = p.maxQty != null && n >= p.maxQty;
      const rating = p.rating ? `<span class="fg-row__rating"><span aria-hidden="true">★</span> ${Number(p.rating).toFixed(1)}${p.ratingCount ? ` <span class="fg-row__rating-count">(${p.ratingCount})</span>` : ''}</span>` : '';
      const why = shown.map((b, i) => {
        const cls = `fg-row__benefit${goals.includes(b) ? ' is-goal' : ''}${i === 3 ? ' fg-row__benefit--desktop' : ''}`;
        return `<span class="${cls}">${goals.includes(b) ? '<span aria-hidden="true">✓</span> ' : ''}${esc(b)}</span>`;
      }).join('');
      return `<article class="fg-row${n ? ' is-in' : ''}" data-card-id="${p.id}">
        <div class="fg-row__img">${p.image ? `<img src="${esc(p.image)}" alt="" loading="lazy" width="300" height="300">` : ''}</div>
        <div class="fg-row__info">
          <h4 class="fg-row__title">${esc(p.title)}${p.seasonal ? ' <span class="fg-row__flag">Seasonal</span>' : ''}</h4>
          ${p.ingredients && p.ingredients.length ? `<p class="fg-row__ingredients">${p.ingredients.map(esc).join(' · ')}</p>` : ''}
          <div class="fg-row__meta">${rating}${why}</div>
          ${this.excluded(p) ? '<p class="fg-row__warn">Contains something you said to skip</p>' : ''}
          <button type="button" class="fg-row__info-link" data-fg-info="${p.id}">Nutrition facts</button>
        </div>
        <div class="fg-row__qty">
          ${n ? `<div class="fg-stepper">
              <button type="button" data-fg-step="-1" data-id="${p.id}" aria-label="Remove one ${esc(p.title)}">−</button>
              <span aria-live="polite">${n}</span>
              <button type="button" data-fg-step="1" data-id="${p.id}" aria-label="Add one ${esc(p.title)}"${atMax ? ' disabled' : ''}>+</button>
            </div>`
            : `<button type="button" class="fg-row__add" data-fg-step="1" data-id="${p.id}" aria-label="Add ${esc(p.title)}"><span aria-hidden="true">+</span> Add</button>`}
        </div>
      </article>`;
    }

    renderList() {
      const goals = this.answers.goals;
      if (this.els.subtitle && this.stage === 'box') {
        this.els.subtitle.textContent = goals.length
          ? `Built around ${goals.slice(0, 3).join(', ')}${goals.length > 3 ? ` + ${goals.length - 3} more` : ''}. Swap anything you like.`
          : 'Our best sellers. Swap anything you like.';
      }
      const hiddenCount = this.products.filter((p) => this.excluded(p)).length;
      // Shown under "Add More Flavors", where the hidden smoothies would otherwise appear.
      this.hiddenNoteHTML = hiddenCount
        ? `${this.showExcluded ? 'Showing' : 'Hiding'} ${hiddenCount} smoothie${hiddenCount > 1 ? 's' : ''} with ingredients you're skipping. <button type="button" class="fg-picker__link" data-fg-show-excluded>${this.showExcluded ? 'Hide them' : 'Show them'}</button>`
        : '';
      this.els.hiddenNote.innerHTML = '';
      const visible = (p) => (this.showExcluded || !this.excluded(p) || this.qty[p.id])
        && (this.filter === 'all' || (p.benefits || []).includes(this.filter));
      const inBox = this.products.filter((p) => this.qty[p.id] && visible(p)).sort((a, b) => this.byMatch(a, b));
      const more = this.products.filter((p) => !this.qty[p.id] && visible(p)).sort((a, b) => this.byMatch(a, b));
      const filterNote = this.filter === 'all' ? '' : ` with ${this.filter}`;
      const noneLeft = !this.eligibleCount();
      let html = this.sectionHTML('box', 'Your Box', inBox,
        inBox.length ? '' : noneLeft ? 'Every flavor contains something you\'re skipping. Change your answers, or show them and pick any you\'re comfortable with.' : (this.filter === 'all' ? 'Your box is empty. Add flavors below.' : `None of your picks are ${this.filter}.`));
      html += this.sectionHTML('more', `Add More Flavors${filterNote}`, more,
        more.length ? 'Swap anything: tap − on one and + on another.' : noneLeft ? '' : 'That\'s every flavor that matches.');
      this.els.list.innerHTML = html;
    }

    sectionHTML(kind, title, items, note) {
      const count = kind === 'box' ? `<span class="fg-group__count" data-fg-box-count>${this.count()} smoothies</span>
        <span class="fg-group__tools">
          <button type="button" class="fg-picker__link" data-fg-retake>Change my answers</button>
          <button type="button" class="fg-picker__link" data-fg-reset>Rebuild my box</button>
          <button type="button" class="fg-picker__link" data-fg-clear>Clear all</button>
        </span>` : '';
      return `<section class="fg-group fg-group--${kind}">
        <header class="fg-group__head"><h3 class="fg-group__title">${esc(title)}</h3>${count}${note ? `<p class="fg-group__desc">${esc(note)}</p>` : ''}${kind === 'more' && this.hiddenNoteHTML ? `<p class="fg-group__desc fg-group__hidden">${this.hiddenNoteHTML}</p>` : ''}</header>
        ${items.length ? `<div class="fg-group__list">${items.map((p) => this.cardHTML(p)).join('')}</div>` : ''}
      </section>`;
    }

    // "Your Free Gifts" strip at the top of the flavor list: the gifts this box
    // unlocks, plus the free-Delite flavor picker (best seller pre-selected).
    choiceGift() {
      const active = this.activeTier(this.count());
      return this.gifts.find((x) => x.choices && x.choices.length && active && this.giftsFor(active.qty).includes(x));
    }

    // Gift choices (e.g. the free Delite) follow the quiz answers: anything with
    // a skipped allergen/ingredient is unavailable, the rest are ranked by how
    // many quiz answers (health goals or flavors) they match, then best-seller order. Until the customer
    // picks one themselves, the top-ranked choice is the selection.
    rankedChoices(g) {
      const rank = (c) => { const i = (g.choiceRank || []).indexOf(c.variantId); return i === -1 ? 999 : i; };
      const score = (c) => (c.benefits || []).filter((b) => this.answers.goals.includes(b)).length;
      return g.choices.slice().sort((a, b) =>
        (this.excluded(a) - this.excluded(b)) || (score(b) - score(a)) || (rank(a) - rank(b)));
    }

    chosenFor(g) {
      const ranked = this.rankedChoices(g);
      const current = g.choices.find((c) => c.variantId === this.giftChoice[g.id]);
      const picked = this.giftPicked && this.giftPicked[g.id];
      const allOut = g.choices.every((c) => this.excluded(c));
      if (!current || !picked || (this.excluded(current) && !allOut)) {
        const best = ranked.find((c) => !this.excluded(c)) || ranked[0];
        this.giftChoice[g.id] = best.variantId;
        return best;
      }
      return current;
    }

    skippedIn(c) {
      const a = this.answers;
      return (c.allergens || []).filter((x) => a.allergens.includes(x)).concat((c.allIngredients || []).filter((x) => a.skip.includes(x)));
    }

    renderGiftChoice() {
      const el = this.els.giftChoice;
      const active = this.activeTier(this.count());
      const unlocked = active ? this.giftsFor(active.qty) : [];
      const g = this.choiceGift();
      if (!unlocked.length) { el.hidden = true; el.innerHTML = ''; return; }
      el.hidden = false;
      const list = unlocked.map((x) => x === g ? `${esc(x.title)} (${esc(this.chosenFor(x).title)})` : esc(x.title)).join(' · ');
      let picker = '';
      if (g) {
        const chosen = this.chosenFor(g);
        const allOut = g.choices.every((c) => this.excluded(c));
        picker = `<p class="fg-gift-choice__title">Pick your free <strong>${esc(g.title)}</strong> flavor</p>
          <div class="fg-gift-choice__row">${this.rankedChoices(g).map((c) => {
            const sel = chosen.variantId === c.variantId;
            const skipped = this.skippedIn(c);
            return `<div class="fg-gift-choice__cell"><button type="button" class="fg-gift-choice__opt${sel ? ' is-selected' : ''}${skipped.length && !allOut ? ' is-unavailable' : ''}" aria-pressed="${sel}"${skipped.length && !allOut ? ' disabled' : ''} data-fg-gift-pick data-gift="${esc(g.id)}" data-variant="${c.variantId}">
              ${c.image ? `<img src="${esc(c.image)}" alt="" loading="lazy" width="300" height="300">` : ''}<span>${esc(c.title)}</span>
              ${skipped.length ? `<small class="fg-gift-choice__why">Contains ${esc(skipped.join(', '))}</small>` : ''}
              ${sel ? '<em class="fg-gift-choice__check" aria-hidden="true">✓</em>' : ''}</button>
              ${c.nutrition ? `<button type="button" class="fg-row__info-link" data-fg-info="${c.variantId}">Nutrition facts</button>` : ''}</div>`;
          }).join('')}</div>${allOut ? '<p class="fg-gift-choice__why-all">Every Delite flavor contains something you\'re skipping. Check the label and pick the one you\'re most comfortable with.</p>' : ''}`;
      }
      el.innerHTML = `<p class="fg-gift-choice__unlocked"><span class="fg-gift-choice__badge">Your free gifts</span> ${list}</p>${picker}`;
    }

    step(id, delta) {
      const p = this.byId.get(id);
      if (!p) return;
      const before = this.count();
      const cur = this.qty[id] || 0;
      let next = Math.max(0, cur + delta);
      if (p.maxQty != null) next = Math.min(next, p.maxQty);
      if (next === cur) return;
      if (next) this.qty[id] = next; else delete this.qty[id];
      this.touched = true;

      // Auto-upgrade the pack when the box grows into a bigger tier.
      const after = this.count();
      const reached = this.activeTier(after);
      const prev = this.activeTier(before);
      if (delta > 0 && reached && reached.qty > this.target) {
        this.target = reached.qty;
        const newGifts = this.giftsFor(reached.qty).filter((g) => !prev || !this.giftsFor(prev.qty).includes(g)).map((g) => g.title);
        this.showToast(`Unlocked ${reached.qty}-pack pricing: ${money(reached.price)}/smoothie${newGifts.length ? ` + ${newGifts.join(', ')}` : ''}!`);
        this.els.chips.innerHTML = this.tiers.map((t) => this.tierCardHTML(t, true)).join('');
      }
      this.afterChange(false, id);
    }

    afterChange(full, id) {
      this.save();
      if (full || id == null) this.renderList();
      else this.root.querySelectorAll(`[data-card-id="${id}"]`).forEach((card) => {
        const p = this.byId.get(id);
        card.outerHTML = this.cardHTML(p);
      });
      this.renderGiftChoice();
      this.renderBar();
    }

    renderBar() {
      const n = this.count();
      const active = this.activeTier(n);
      const target = this.tierFor(this.target);
      const maxQty = this.tiers[this.tiers.length - 1].qty;
      this.els.pickerTitle.innerHTML = `Pick your <strong>${this.target} smoothies</strong>`;
      this.els.progress.style.width = Math.min(100, (n / this.target) * 100) + '%';
      this.els.countEl.textContent = `${n}/${this.target}`;
      const boxCount = this.root.querySelector('[data-fg-box-count]');
      if (boxCount) boxCount.textContent = `${n} smoothies`;

      let msg;
      if (n < this.minQty) msg = `Add ${this.minQty - n} more to check out`;
      else if (n < this.target) msg = `Add ${this.target - n} more for ${this.target}-pack pricing (${money(target.price)}/smoothie)`;
      else {
        const next = this.tiers.find((t) => t.qty > n);
        msg = `${money(active.price)}/smoothie` + (next && next.qty - n <= 5 ? ` · ${next.qty - n} more unlocks ${money(next.price)}` : '');
      }
      this.els.barMsg.textContent = msg;

      const unlocked = active ? this.giftsFor(active.qty) : [];
      const saved = this.savingsFor(n, active);
      const cg = this.choiceGift();
      const giftText = unlocked.map((g) => g === cg ? `${esc(g.title)}: <strong>${esc(this.chosenFor(g).title)}</strong> <button type="button" class="fg-picker__link fg-bar__change" data-fg-gift-jump>Change</button>` : esc(g.title)).join(', ');
      this.els.barGifts.innerHTML = [saved ? `You save ${money(saved)}` : '', giftText].filter(Boolean).join(' · ');

      const ok = n >= this.minQty;
      this.els.checkout.disabled = !ok;
      this.els.checkoutLabel.textContent = ok ? `Checkout · ${money(this.totalFor(n, active))}` : 'Checkout';
      if (n > maxQty) this.els.countEl.textContent = `${n}`;
    }

    showToast(text) {
      const el = this.els.toast;
      el.textContent = text;
      el.classList.add('is-visible');
      clearTimeout(this._toastT);
      this._toastT = setTimeout(() => el.classList.remove('is-visible'), 3500);
    }

    /* ---------- checkout ---------- */

    async checkout() {
      const n = this.count();
      const active = this.activeTier(n);
      if (!active) return;
      const btn = this.els.checkout;
      btn.disabled = true;
      btn.setAttribute('data-loading', '');

      const offerTag = `New Customer ${active.qty}-Pack`;
      const lines = Object.entries(this.qty).filter(([, q]) => q > 0).map(([id, q]) => ({
        id: this.byId.get(+id).variantId, quantity: q, properties: { _offer: offerTag }
      }));
      this.giftsFor(active.qty).forEach((g) => {
        const variant = (g.choices && g.choices.length) ? this.chosenFor(g).variantId : g.variantId;
        if (variant) lines.push({ id: variant, quantity: 1, properties: { _offer: offerTag, _offer_gift: g.title } });
      });

      try {
        // cart/clear is the one cart endpoint global.js lets through.
        await fetch((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart/clear.js', { method: 'POST' });
      } catch (e) { /* a failed clear still lets the add go through; checkout shows the real cart */ }

      try { sessionStorage.setItem('bypass_cart_redirect', 'true'); } catch (e) { /* ignore */ }

      const form = document.createElement('form');
      form.method = 'POST';
      form.action = (window.routes && window.routes.cart_add_url) || '/cart/add';
      form.style.display = 'none';
      const add = (name, value) => { const i = document.createElement('input'); i.type = 'hidden'; i.name = name; i.value = value; form.appendChild(i); };
      add('return_to', active.code ? `/checkout?discount=${active.code.split(',').map((c) => encodeURIComponent(c.trim())).filter(Boolean).join(',')}` : '/checkout');
      lines.forEach((l, i) => {
        add(`items[${i}][id]`, l.id);
        add(`items[${i}][quantity]`, l.quantity);
        Object.entries(l.properties).forEach(([k, v]) => add(`items[${i}][properties][${k}]`, v));
      });
      document.body.appendChild(form);
      form.submit();
    }
  }

  const init = () => document.querySelectorAll('[data-fg-offer]').forEach((el) => { if (!el._fgOffer) el._fgOffer = new FGOffer(el); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
})();
